import React from 'react';
import styled from "@emotion/styled";
import { XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, CartesianGrid } from 'recharts';

const formatCurrency = (value) => (
  value.toLocaleString("en-US", {maximumFractionDigits: 2, minimumFractionDigits: 2})
)

const roundToWholeThousand = (value) => Math.round(value / 1000) * 1000

const buildAxisTicks = (data, getValue, multiplier) => {
  const values = Array.isArray(data) ? data.map((item) => Number(getValue(item)) || 0) : []
  const maxValue = values.length ? Math.max(...values) : 0
  const visualMax = maxValue > 0 ? maxValue * multiplier : 3000
  const step = Math.max(1000, Math.ceil(visualMax / 3 / 1000) * 1000)

  return [0, step, step * 2, step * 3]
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload

    return (
      <TooltipSurface>
        <TooltipHeader>
          <TooltipKicker>Payment Snapshot</TooltipKicker>
          <TooltipTitle>{data.paymentDate}</TooltipTitle>
        </TooltipHeader>
        <TooltipRows>
          <TooltipRow className="strong">
            <TooltipLabel>Principal paid</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.principalPaid)}</TooltipValue>
          </TooltipRow>
          <TooltipRow className="strong">
            <TooltipLabel>Interest paid</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.interestPaid)}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Ratio (i/p)</TooltipLabel>
            <TooltipValue>{(data.interestPaid/data.principalPaid).toLocaleString("en-US", {maximumFractionDigits: 3, minimumFractionDigits: 3})}</TooltipValue>
          </TooltipRow>
          {data.principalPayments > 0 &&
            <PrincipalOnlyRow>
              <span>Principal only payments</span>
              <strong><Accent>$</Accent>{formatCurrency(data.principalPayments)}</strong>
            </PrincipalOnlyRow>
          }

          <TooltipDivider />

          <TooltipRow className="strong">
            <TooltipLabel>Total principal paid</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalPrincipalPaid)}</TooltipValue>
          </TooltipRow>
          <TooltipRow className="strong">
            <TooltipLabel>Total interest paid</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.totalInterestPaid)}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Ratio (i/p)</TooltipLabel>
            <TooltipValue>{(data.totalInterestPaid/data.totalPrincipalPaid).toLocaleString("en-US", {maximumFractionDigits: 3, minimumFractionDigits: 3})}</TooltipValue>
          </TooltipRow>

          <TooltipDivider />

          <TooltipRow className="strong">
            <TooltipLabel>Principal remaining</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.principalRemaining)}</TooltipValue>
          </TooltipRow>
          <TooltipRow className="strong">
            <TooltipLabel>Interest remaining</TooltipLabel>
            <TooltipValue><Accent>$</Accent>{formatCurrency(data.interestRemaining)}</TooltipValue>
          </TooltipRow>
          <TooltipRow>
            <TooltipLabel>Ratio (i/p)</TooltipLabel>
            <TooltipValue>{(data.interestRemaining/data.principalRemaining).toLocaleString("en-US", {maximumFractionDigits: 3, minimumFractionDigits: 3})}</TooltipValue>
          </TooltipRow>
        </TooltipRows>
      </TooltipSurface>
    )
  }

  return null
}

const MortgagePaymentsChart = ({ paymentsToView }) => {
  const amountRemainingTicks = buildAxisTicks(
    paymentsToView,
    (item) => item.interestRemaining + item.principalRemaining,
    1.2
  )
  const amountPaidTicks = buildAxisTicks(
    paymentsToView,
    (item) => item.totalPrincipalPaid + item.totalInterestPaid,
    1.2
  )

  return (
    <ChartBounds>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={paymentsToView}
          layout="horizontal"
          margin={{
            top: 12,
            right: 48,
            left: 48,
            bottom: 4,
          }}
        >
          <CartesianGrid stroke="#edf2f7" vertical={false} />
          <XAxis tick={{ fill: "#64748b", fontSize: 12, fontWeight: 700 }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <YAxis 
            type="number" 
            yAxisId="left" 
            stroke="#64748b" 
            orientation="right" 
            width={78}
            label={{ value: 'Amount Remaining', angle: 90, fontWeight: '800', fill: '#475569', dy: -72, dx: 48}} 
            ticks={amountRemainingTicks}
            domain={[0, amountRemainingTicks[3]]}
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 700 }}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={false}
            tickFormatter={tick => {
              return `$${roundToWholeThousand(tick).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}`
            }}
          />
          <YAxis 
            type="number"
            yAxisId="right"
            stroke="#64748b"
            orientation="left"
            width={78}
            label={{ value: 'Amount Paid', angle: -90, fontWeight: '800', fill: '#475569', dy: -72, dx: -48 }}
            ticks={amountPaidTicks}
            domain={[0, amountPaidTicks[3]]}
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 700 }}
            axisLine={{ stroke: "#cbd5e1" }}
            tickLine={false}
            tickFormatter={tick => {
              return `$${roundToWholeThousand(tick).toLocaleString("en-US", {maximumFractionDigits: 0, minimumFractionDigits: 0})}`
            }}
          />
          <Tooltip content={<CustomTooltip/>} cursor={{fill: `rgba(147, 149, 153, 0.12)`}} />
          <Legend wrapperStyle={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155", paddingTop: "0.35rem" }} />
          <defs>
            <linearGradient id="paymentsColorPrincipal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4768b0" stopOpacity={0.78}/>
              <stop offset="42%" stopColor="#6E88CC" stopOpacity={0.28}/>
              <stop offset="96%" stopColor="#6E88CC" stopOpacity={0.06}/>
            </linearGradient>
            <linearGradient id="paymentsColorInterest" x1="0" y1="0" x2="0" y2="1">
              <stop offset="4%" stopColor="#7d9fe4" stopOpacity={0.58}/>
              <stop offset="58%" stopColor="#A0B7F2" stopOpacity={0.18}/>
              <stop offset="96%" stopColor="#A0B7F2" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="paymentsColorPrincipalRemaining" x1="0" y1="0" x2="0" y2="1">
              <stop offset="4%" stopColor="#167a3a" stopOpacity={0.72}/>
              <stop offset="36%" stopColor="#167a3a" stopOpacity={0.26}/>
              <stop offset="58%" stopColor="#167a3a" stopOpacity={0.1}/>
              <stop offset="96%" stopColor="#167a3a" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="paymentsColorInterestRemaining" x1="0" y1="0" x2="0" y2="1">
              <stop offset="4%" stopColor="#2f9e73" stopOpacity={0.6}/>
              <stop offset="64%" stopColor="#2f9e73" stopOpacity={0.2}/>
              <stop offset="96%" stopColor="#2f9e73" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            yAxisId="right"
            dataKey="totalPrincipalPaid"
            stackId="b"
            stroke="#4768b0"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#paymentsColorPrincipal)"
            activeDot={{ r: 3.5, stroke: "#ffffff", strokeWidth: 1.5, fill: "#4768b0" }}
            name="Total Principal Paid"
          />
          <Area
            type="monotone"
            yAxisId="right"
            dataKey="totalInterestPaid"
            stackId="b"
            stroke="#7d9fe4"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#paymentsColorInterest)"
            activeDot={{ r: 3.5, stroke: "#ffffff", strokeWidth: 1.5, fill: "#7d9fe4" }}
            name="Total Interest Paid"
          />
          <Area
            type="monotone"
            yAxisId="left"
            dataKey="principalRemaining"
            stackId="a"
            stroke="#167a3a"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#paymentsColorPrincipalRemaining)"
            activeDot={{ r: 3.5, stroke: "#ffffff", strokeWidth: 1.5, fill: "#167a3a" }}
            name="Principal Remaining"
          />
          <Area
            type="monotone"
            yAxisId="left"
            dataKey="interestRemaining"
            stackId="a"
            stroke="#2f9e73"
            strokeWidth={2.2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="url(#paymentsColorInterestRemaining)"
            activeDot={{ r: 3.5, stroke: "#ffffff", strokeWidth: 1.5, fill: "#2f9e73" }}
            name="Interest Remaining"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartBounds>
  )
}

const ChartBounds = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`

const TooltipSurface = styled.div(({ theme }) => `
  width: 20rem;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.75rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.85rem 1.9rem rgba(15, 23, 42, 0.16);
`)

const TooltipHeader = styled.div(({ theme }) => `
  padding: 0.78rem 0.9rem 0.68rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const TooltipKicker = styled.div(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const TooltipTitle = styled.div(({ theme }) => `
  margin-top: 0.15rem;
  color: ${theme.palette.common.black};
  font-size: 1.04rem;
  font-weight: 900;
  line-height: 1.25;
`)

const TooltipRows = styled.div`
  display: grid;
  gap: 0.04rem;
  padding: 0.5rem 0.9rem 0.72rem;
`

const TooltipRow = styled.div(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.82rem;
  align-items: baseline;
  padding: 0.4rem 0;

  &.strong span:last-of-type {
    font-weight: 950;
  }
`)

const TooltipLabel = styled.span(({ theme }) => `
  min-width: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
`)

const TooltipValue = styled.span(({ theme }) => `
  display: inline-flex;
  align-items: baseline;
  gap: 0.12rem;
  color: ${theme.palette.common.black};
  font-size: 0.9rem;
  font-weight: 850;
  line-height: 1.2;
  text-align: right;
`)

const PrincipalOnlyRow = styled.div(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.7rem;
  align-items: center;
  margin: 0.22rem 0;
  padding: 0.45rem 0.55rem;
  border: 0.1rem solid ${theme.palette.primary.main};
  border-radius: 0.55rem;
  background: ${theme.palette.primary.labelTransparent};
  color: ${theme.palette.primary.dark};
  font-size: 0.78rem;
  font-weight: 850;

  strong {
    display: inline-flex;
    align-items: baseline;
    gap: 0.12rem;
    color: ${theme.palette.common.black};
    font-size: 0.88rem;
    font-weight: 950;
  }
`)

const TooltipDivider = styled.div(({ theme }) => `
  height: 0.1rem;
  margin: 0.22rem 0;
  background: ${theme.palette.common.grayBorderLight};
`)

const Accent = styled.span(({ theme }) => `
  color: ${theme.palette.primary.pastelMedium};
  font-size: 0.76rem;
  font-weight: 900;
`)

export default MortgagePaymentsChart
